import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/company/companyEdit.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import ProductList from '../../pages/ProductList';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { getCompanyById, updateCompany, getCompanyWarehouseById, updateCompanyWarehouse, warehouseList, getCountries, getStates, getCities, getCompanyReport } from '../../services/Api';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import { useDispatch } from 'react-redux';
import Loader from '../../utils/Loader';
import { AiOutlineLoading } from 'react-icons/ai';

const CompanyEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('company');
    const [warehouseLoading, setWarehouseLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });

    const [companyData, setCompanyData] = useState({
        id: '',
        customer_id: '',
        company_name: '',
        company_uen_number: '',
        email: '',
        contact_number: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        main_contact_person_name: '',
        main_contact_person_email: '',
        main_contact_person_phone_number: '',
        remark: '',
        status: '',
        password: null,
        company_warehouses: []
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [reportData, setReportData] = useState({
        sales_analysis: {},
        total_stock_movment: 0
    });
    const [reportLoading, setReportLoading] = useState(false);

    const [editingWarehouse, setEditingWarehouse] = useState(null);
    const [editWarehouseData, setEditWarehouseData] = useState({
        membership_till: '',
        warehouse_id: '',
        min_lot: '',
        max_lot: ''
    });

    const [isAddingWarehouse, setIsAddingWarehouse] = useState(false);
    const [newWarehouseData, setNewWarehouseData] = useState({
        membership_till: '',
        warehouse_id: '',
        min_lot: '',
        max_lot: ''
    });

    const handleSetPrimary = async (warehouseIndex) => {
        try {
            // Update local state first
            const updatedWarehouses = companyData.company_warehouses.map((warehouse, index) => ({
                ...warehouse,
                is_primary: index === warehouseIndex ? 1 : 0
            }));

            setCompanyData(prev => ({
                ...prev,
                company_warehouses: updatedWarehouses
            }));

            // You can also call API here to update immediately if needed
            showPopup('success', 'Success', 'Primary warehouse updated successfully');
        } catch (error) {
            console.error("Error setting primary warehouse:", error);
            showPopup('error', 'Error', 'Failed to set primary warehouse');
        }
    };

    const handleEditWarehouse = (warehouseIndex) => {
        const warehouse = companyData.company_warehouses[warehouseIndex];
        setEditingWarehouse(warehouseIndex);
        setEditWarehouseData({
            membership_till: warehouse.membership_till,
            warehouse_id: warehouse.warehouse_id,
            min_lot: warehouse.min_lot.toString(),
            max_lot: warehouse.max_lot.toString()
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'min_lot' || name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            setEditWarehouseData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setEditWarehouseData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAddWarehouse = () => {
        setIsAddingWarehouse(true);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    const handleNewWarehouseInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'min_lot' || name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            setNewWarehouseData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setNewWarehouseData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAddNewWarehouse = () => {
        setIsAddingWarehouse(true);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    const handleSaveNewWarehouse = () => {
        // Validate the new warehouse data
        if (!newWarehouseData.membership_till || !newWarehouseData.warehouse_id ||
            !newWarehouseData.min_lot || !newWarehouseData.max_lot) {
            showPopup('error', 'Error', 'Please fill all required fields');
            return;
        }

        // Check if min_lot is less than max_lot
        if (parseInt(newWarehouseData.min_lot) >= parseInt(newWarehouseData.max_lot)) {
            showPopup('error', 'Error', 'Maximum lot must be greater than minimum lot');
            return;
        }

        // Check if warehouse already exists for this company
        const warehouseExists = companyData.company_warehouses.some(
            warehouse => warehouse.warehouse_id === parseInt(newWarehouseData.warehouse_id)
        );

        if (warehouseExists) {
            showPopup('error', 'Error', 'This warehouse is already assigned to the company');
            return;
        }

        // Get warehouse name from warehouses array
        const selectedWarehouse = warehouses.find(w => w.id === parseInt(newWarehouseData.warehouse_id));
        const warehouseName = selectedWarehouse ? selectedWarehouse.name : 'Unknown Warehouse';

        // Add the new warehouse to the existing list
        const newWarehouse = {
            id: null, // This will be set by backend after successful creation
            company_id: companyData.id,
            warehouse_id: parseInt(newWarehouseData.warehouse_id),
            name: warehouseName,
            membership_till: newWarehouseData.membership_till,
            min_lot: parseInt(newWarehouseData.min_lot),
            max_lot: parseInt(newWarehouseData.max_lot),
            is_primary: companyData.company_warehouses.length === 0 ? 1 : 0
        };

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: [...prev.company_warehouses, newWarehouse]
        }));

        setIsAddingWarehouse(false);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });

        showPopup('success', 'Success', 'New warehouse added successfully. Click Update to save changes.');
    };

    const handleSaveEdit = () => {
        const updatedWarehouses = [...companyData.company_warehouses];
        updatedWarehouses[editingWarehouse] = {
            ...updatedWarehouses[editingWarehouse],
            membership_till: editWarehouseData.membership_till,
            warehouse_id: editWarehouseData.warehouse_id,
            min_lot: parseInt(editWarehouseData.min_lot),
            max_lot: parseInt(editWarehouseData.max_lot)
        };

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: updatedWarehouses
        }));

        setEditingWarehouse(null);
        setEditWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    const handleCancelNewWarehouse = () => {
        setIsAddingWarehouse(false);
        setNewWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    const handleCancelEdit = () => {
        setEditingWarehouse(null);
        setEditWarehouseData({
            membership_till: '',
            warehouse_id: '',
            min_lot: '',
            max_lot: ''
        });
    };

    useEffect(() => {
        const fetchCompanyData = async () => {
            setIsLoading(true);
            try {
                const data = await getCompanyById(id);
                setCompanyData({
                    ...companyData,
                    id: data.data.id,
                    customer_id: data.data.customer_id,
                    company_name: data.data.company_name,
                    company_uen_number: data.data.company_uen_number,
                    email: data.data.email,
                    contact_number: data.data.contact_number,
                    address: data.data.address,
                    country_id: data.data.country_id,
                    state_id: data.data.state_id,
                    city_id: data.data.city_id,
                    main_contact_person_name: data.data.main_contact_person_name,
                    main_contact_person_email: data.data.main_contact_person_email,
                    main_contact_person_phone_number: data.data.main_contact_person_phone_number,
                    remark: data.data.remark,
                    status: data.data.status,
                    company_warehouses: []
                    // company_warehouses: data.data.company_warehouses || []
                });
                fetchCompanyDataWarehouse(data.data.id);

                await fetchCountries();
                if (data.data.country_id) {
                    await fetchStates(data.data.country_id);
                }
                if (data.data.state_id) {
                    await fetchCities(data.data.state_id);
                }
            } catch (error) {
                console.error("Error fetching company data:", error);
                showPopup('error', 'Error', 'Failed to fetch company data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompanyData();
    }, [id]);

    const fetchCompanyDataWarehouse = async (companyId) => {
        try {
            const warehouseData = await getCompanyWarehouseById(companyId);
            if (warehouseData && warehouseData.data) {
                setCompanyData(prevData => ({
                    ...prevData,
                    company_warehouses: warehouseData.data || []
                }));
            }
        } catch (error) {
            console.error("Error fetching warehouse data:", error);
            showPopup('error', 'Error', 'Failed to fetch warehouse information');
        }
    };

    const fetchCompanyReport = async () => {
        setReportLoading(true);
        try {
            const response = await getCompanyReport(id);
            if (response.status === 200) {
                setReportData(response.data);
            }
        } catch (error) {
            console.error("Error fetching company report:", error);
            showPopup('error', 'Error', 'Failed to fetch company report');
        } finally {
            setReportLoading(false);
        }
    };

    // Function to fetch countries
    const fetchCountries = async () => {
        setLoadingLocations(prev => ({ ...prev, countries: true }));
        try {
            const response = await getCountries();
            // Based on the API response: { data: [countries], message: "message.list_country" }
            if (response && response.data && Array.isArray(response.data.data)) {
                setCountries(response.data.data);
            } else {
                console.error("Countries data not in expected format:", response);
                setCountries([]);
                showPopup('error', 'Error', 'Failed to parse countries data');
            }
        } catch (error) {
            console.error("Error fetching countries:", error);
            setCountries([]);
            showPopup('error', 'Error', 'Failed to load countries');
        } finally {
            setLoadingLocations(prev => ({ ...prev, countries: false }));
        }
    };

    // Function to fetch states based on selected country
    const fetchStates = async (countryId) => {
        if (!countryId) return;

        setLoadingLocations(prev => ({ ...prev, states: true }));
        setStates([]);
        setCities([]);

        try {
            const response = await getStates(countryId);

            if (response && response.data && Array.isArray(response.data.data)) {
                setStates(response.data.data);
            } else {
                console.error("States data not in expected format:", response);
                setStates([]);
                showPopup('error', 'Error', 'Failed to parse states data');
            }
        } catch (error) {
            console.error("Error fetching states:", error);
            setStates([]);
            showPopup('error', 'Error', 'Failed to load states');
        } finally {
            setLoadingLocations(prev => ({ ...prev, states: false }));
        }
    };

    // Function to fetch cities based on selected state
    const fetchCities = async (stateId) => {
        if (!stateId) return;

        setLoadingLocations(prev => ({ ...prev, cities: true }));
        setCities([]);

        try {
            const response = await getCities(stateId);
            if (response && response.data && Array.isArray(response.data.data)) {
                setCities(response.data.data);
            } else {
                console.error("Cities data not in expected format:", response);
                setCities([]);
                showPopup('error', 'Error', 'Failed to parse cities data');
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
            setCities([]);
            showPopup('error', 'Error', 'Failed to load cities');
        } finally {
            setLoadingLocations(prev => ({ ...prev, cities: false }));
        }
    };

    const validationRules = {
        company_name: {
            required: true,
            message: 'Company name is required'
        },
        company_uen_number: {
            required: true,
            message: 'Company UEN number is required'
        },
        email: {
            required: true,
            message: 'Email is required',
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            patternMessage: 'Please enter a valid email address'
        },
        contact_number: {
            required: true,
            message: 'Contact number is required',
            pattern: /^\d{10}$/,
            patternMessage: 'Phone number must be exactly 10 digits'
        },
        address: {
            required: true,
            message: 'Address is required'
        },
        country_id: {
            required: true,
            message: 'Country is required'
        },
        state_id: {
            required: true,
            message: 'State is required'
        },
        city_id: {
            required: true,
            message: 'City is required'
        },
        main_contact_person_name: {
            required: true,
            message: 'Contact person name is required'
        },
        main_contact_person_email: {
            required: true,
            message: 'Contact person email is required',
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            patternMessage: 'Please enter a valid email address'
        },
        main_contact_person_phone_number: {
            required: true,
            message: 'Contact person phone number is required',
            pattern: /^\d{10}$/,
            patternMessage: 'Phone number must be exactly 10 digits'
        },
        remark: {
            required: false,
            message: ''
        },
        status: {
            required: true,
            message: 'Please select a status'
        }
    };

    const warehouseValidationRules = {
        membership_till: {
            required: true,
            message: 'Membership date is required'
        },
        warehouse_id: {
            required: true,
            message: 'Warehouse location is required'
        },
        min_lot: {
            required: true,
            message: 'Warehouse minimum lot is required'
        },
        max_lot: {
            required: true,
            message: 'Warehouse maximum lot is required'
        },
        is_primary: {
            required: false,
            message: ''
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for phone numbers and status
        if (name === 'contact_number' || name === 'main_contact_person_phone_number') {
            if (value !== '') {
                const numericValue = value.replace(/\D/g, '');
                // Limit to 10 digits
                const truncatedValue = numericValue.slice(0, 10);

                setCompanyData(prev => ({
                    ...prev,
                    [name]: truncatedValue
                }));
            } else {
                setCompanyData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'status') {
            // Make sure status is set explicitly even when it's "0"
            setCompanyData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name === 'country_id') {
            // When country changes, reset state and city values
            setCompanyData(prev => ({
                ...prev,
                [name]: value,
                state_id: '',
                city_id: ''
            }));

            // Fetch states based on selected country
            fetchStates(value);

        } else if (name === 'state_id') {
            // When state changes, reset city value and fetch cities
            setCompanyData(prev => ({
                ...prev,
                [name]: value,
                city_id: ''
            }));

            // Fetch cities based on selected state
            fetchCities(value);

        } else {
            setCompanyData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        const selectElement = e.target;
        if (value !== '') {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        for (const field in validationRules) {
            // Check required fields - ensure status is treated as a valid value when it's 0 (inactive)
            if (validationRules[field].required &&
                (field === 'status' ? companyData[field] === '' : !companyData[field])) {
                newErrors[field] = validationRules[field].message;
            }
            // Check pattern validation if field has value
            else if (companyData[field] && validationRules[field].pattern &&
                !validationRules[field].pattern.test(companyData[field])) {
                newErrors[field] = validationRules[field].patternMessage;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);

        // If switching to warehouse tab and data hasn't been loaded yet, show loading state
        if (tab === 'warehouse' && companyData.company_warehouses.length === 0) {
            setWarehouseLoading(true);
            fetchCompanyDataWarehouse(companyData.id).then(() => {
                setWarehouseLoading(false);
            });
        }

        if (tab === 'report') {
            fetchCompanyReport();
        }
    };

    const handleCompanyUpdate = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const updateData = {
                ...companyData,
                password: companyData.password || null,
            };

            const response = await updateCompany(updateData);
            if (response.status === 200) {
                showPopup('success', 'Success', 'Company updated successfully');
                navigate("/company");
            }
        } catch (error) {
            console.error("Update error:", error);
            const errorMessage = error.response?.data?.message || 'Failed to update company';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    const validateWarehouseForm = () => {
        const newErrors = {};
        companyData.company_warehouses.forEach((warehouse, index) => {
            for (const field in warehouseValidationRules) {
                // Check required fields
                if (warehouseValidationRules[field].required && !warehouse[field]) {
                    newErrors[`warehouse_${index}_${field}`] = warehouseValidationRules[field].message;
                }

                // Additional numeric validation for min_lot and max_lot
                if ((field === 'min_lot' || field === 'max_lot') &&
                    warehouse[field] && !/^\d+$/.test(warehouse[field])) {
                    newErrors[`warehouse_${index}_${field}`] = `${field === 'min_lot' ? 'Minimum' : 'Maximum'} lot must be a number`;
                }

                // Validate min_lot is less than max_lot if both exist
                if (field === 'max_lot' && warehouse.min_lot && warehouse.max_lot &&
                    parseInt(warehouse.min_lot) > parseInt(warehouse.max_lot)) {
                    newErrors[`warehouse_${index}_max_lot`] = 'Maximum lot must be greater than minimum lot';
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleWarehouseUpdate = async () => {
        if (!validateWarehouseForm()) return;

        setLoading(true);
        try {
            // Prepare all warehouses (both existing and new) for the API
            const allWarehouses = companyData.company_warehouses.map(warehouse => ({
                company_id: companyData.id,
                warehouse_id: warehouse.warehouse_id,
                membership_till: warehouse.membership_till,
                min_lot: parseInt(warehouse.min_lot),
                max_lot: parseInt(warehouse.max_lot),
                is_primary: warehouse.is_primary === 1 || warehouse.is_primary === true
            }));

            const updateData = {
                company_warehouses: allWarehouses
            };

            const response = await updateCompanyWarehouse(updateData);

            if (response && response.status === 200) {
                showPopup('success', 'Success', 'Warehouse updated successfully');

                // Refresh the warehouse data to get the latest state including new IDs
                await fetchCompanyDataWarehouse(companyData.id);

                navigate("/company");
            }
        } catch (error) {
            console.error("Warehouse update error:", error);
            const errorMessage = error.response?.data?.message || 'Failed to update warehouse';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
            }
        };
        fetchWarehouses();
    }, []);

    const handleWarehouseInputChange = (e, index) => {
        const { name, value } = e.target;
        const updatedWarehouses = [...companyData.company_warehouses];

        // Handle numeric fields
        if (name === 'min_lot' || name === 'max_lot') {
            const numericValue = value.replace(/\D/g, '');
            updatedWarehouses[index] = {
                ...updatedWarehouses[index],
                [name]: numericValue
            };
        } else {
            updatedWarehouses[index] = {
                ...updatedWarehouses[index],
                [name]: value
            };
        }

        setCompanyData(prev => ({
            ...prev,
            company_warehouses: updatedWarehouses
        }));

        if (errors[`warehouse_${index}_${name}`]) {
            setErrors(prev => ({
                ...prev,
                [`warehouse_${index}_${name}`]: ''
            }));
        }
    };

    // Replace the existing testData with dynamic data
    const salesAnalysisData = Object.entries(reportData.sales_analysis).map(([name, value], index) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: parseInt(value),
        color: index === 0 ? "#2D2A83" : "#C2C3F2"
    }));

    const stockData = [
        { name: "Jan", stock: 100 },
        { name: "Feb", stock: 120 },
        { name: "Mar", stock: 90 },
        { name: "Apr", stock: 130 },
        { name: "May", stock: 110 },
        { name: "Jun", stock: 105 },
        { name: "Jul", stock: 125 },
    ];

    const profitLossData = [
        { month: "Jan", shopify: 30, lazada: 25 },
        { month: "Feb", shopify: 45, lazada: 35 },
        { month: "Mar", shopify: 32, lazada: 30 },
        { month: "Apr", shopify: 50, lazada: 40 },
        { month: "May", shopify: 28, lazada: 35 },
        { month: "Jun", shopify: 20, lazada: 10 },
        { month: "Jul", shopify: 45, lazada: 22 },
    ];

    if (isLoading) {
        return (
            <div className={`${Styles.loader_container}`}>
                <Loader size="large" color="primary" />
            </div>
        );
    }

    return (
        <div className={`${Styles.company_edit}`}>
            <div className={`${Styles.heading}`}>
                <h2>Company Information</h2>
                <button className={`${Styles.add_btn}`} onClick={() => navigate("/company")}>Back</button>
            </div>

            <div className={`${Styles.company_form}`}>
                <div className={`${Styles.handle_heading}`}>
                    <div className={`${Styles.handle_form}`}>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('company')} className={`${activeTab === 'company' ? Styles.active : ''}`}>Company details</h4>
                            {activeTab === 'company' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('warehouse')} className={`${activeTab === 'warehouse' ? Styles.active : ''}`}>Warehouse details</h4>
                            {activeTab === 'warehouse' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('product')} className={`${activeTab === 'product' ? Styles.active : ''}`}>Product in Stock</h4>
                            {activeTab === 'product' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('report')} className={`${activeTab === 'report' ? Styles.active : ''}`}>Reports</h4>
                            {activeTab === 'report' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                    </div>
                    <hr />
                </div>

                {activeTab === 'company' && (
                    <div className={`${Styles.company_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Company details</h4>
                            <hr />
                        </div>
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input}`}>
                                <label>Customer ID (auto-generated)</label>
                                <input type="text" value={companyData.customer_id} disabled />
                            </div>
                            <div className={`${Styles.label_input} ${errors.company_name ? Styles.error : ''}`}>
                                <label>Company name<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input type="text" placeholder="Enter Company Name" name="company_name" value={companyData.company_name} onChange={handleInputChange} />
                                    {errors.company_name && <span className={`${Styles.errorMessage}`}>{errors.company_name}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.company_uen_number ? Styles.error : ''}`}>
                                <label>Company UEN number<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input type="text" placeholder="Enter UEN Number" name="company_uen_number" value={companyData.company_uen_number} onChange={handleInputChange} />
                                    {errors.company_uen_number && <span className={`${Styles.errorMessage}`}>{errors.company_uen_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.contact_number ? Styles.error : ''}`}>
                                <label>Phone number<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input type="text" placeholder="Enter Phone number" name="contact_number" value={companyData.contact_number} onChange={handleInputChange} />
                                    {errors.contact_number && <span className={`${Styles.errorMessage}`}>{errors.contact_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.email ? Styles.error : ''}`}>
                                <label>Email<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input type="text" placeholder="Enter Email" name="email" value={companyData.email} onChange={handleInputChange} disabled />
                                    {errors.email && <span className={`${Styles.errorMessage}`}>{errors.email}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                <label>Address<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input type="text" placeholder="Enter Address" name="address" value={companyData.address} onChange={handleInputChange} />
                                    {errors.address && <span className={`${Styles.errorMessage}`}>{errors.address}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.status ? Styles.error : ''}`}>
                                <label>Status<span>*</span></label>
                                <div className={`${Styles.select_status}`}>
                                    <select className={`${Styles.company_status}`} name="status" value={companyData.status} onChange={handleInputChange}>
                                        <option value="" hidden>Select Status</option>
                                        <option value="1">Active</option>
                                        <option value="2">Inactive</option>
                                    </select>
                                    {errors.status && <span className={`${Styles.errorMessage}`}>{errors.status}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input}`}>
                                <label>Country /State /City<span>*</span></label>
                                <div className={`${Styles.select_container}`}>
                                    <select className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`} name="country_id" value={companyData.country_id} onChange={handleInputChange} disabled={loadingLocations.countries}>
                                        <option value="" hidden>Select Country</option>
                                        {loadingLocations.countries ? (
                                            <option value="" disabled>Loading countries...</option>
                                        ) : Array.isArray(countries) && countries.length > 0 ? (
                                            countries.map(country => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No countries available</option>
                                        )}
                                    </select>
                                    {errors.country_id && <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>}

                                    <select className={`${Styles.state} ${errors.state_id ? Styles.error : ''}`} name="state_id" value={companyData.state_id} onChange={handleInputChange} disabled={!companyData.country_id || loadingLocations.states}>
                                        <option value="" hidden>Select State</option>
                                        {loadingLocations.states ? (
                                            <option value="" disabled>Loading states...</option>
                                        ) : Array.isArray(states) && states.length > 0 ? (
                                            states.map(state => (
                                                <option key={state.id} value={state.id}>
                                                    {state.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No states available</option>
                                        )}
                                    </select>
                                    {errors.state_id && <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>}

                                    <select className={`${Styles.city} ${errors.city_id ? Styles.error : ''}`} name="city_id" value={companyData.city_id} onChange={handleInputChange} disabled={!companyData.state_id || loadingLocations.cities}>
                                        <option value="" hidden>Select City</option>
                                        {loadingLocations.cities ? (
                                            <option value="" disabled>Loading cities...</option>
                                        ) : Array.isArray(cities) && cities.length > 0 ? (
                                            cities.map(city => (
                                                <option key={city.id} value={city.id}>
                                                    {city.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>No cities available</option>
                                        )}
                                    </select>
                                    {errors.city_id && <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>}
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.contact_person}`}>
                            <div className={`${Styles.title2}`}>
                                <h1>Main contact person</h1>
                                <hr />
                            </div>
                            <div className={`${Styles.form}`}>
                                <div className={`${Styles.label_input} ${errors.main_contact_person_name ? Styles.error : ''}`}>
                                    <label>Contact name person<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Name Person" name="main_contact_person_name" value={companyData.main_contact_person_name} onChange={handleInputChange} />
                                        {errors.main_contact_person_name && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_name}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.main_contact_person_email ? Styles.error : ''}`}>
                                    <label>Email<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Email" name="main_contact_person_email" value={companyData.main_contact_person_email} onChange={handleInputChange} />
                                        {errors.main_contact_person_email && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_email}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.main_contact_person_phone_number ? Styles.error : ''}`}>
                                    <label>Phone number<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Phone number" name="main_contact_person_phone_number" value={companyData.main_contact_person_phone_number} onChange={handleInputChange} />
                                        {errors.main_contact_person_phone_number && <span className={`${Styles.errorMessage}`}>{errors.main_contact_person_phone_number}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input}`}>
                                    <label>Remark</label>
                                    <div className={`${Styles.select_field}`}>
                                        <input type="text" placeholder="Enter Remark" name="remark" value={companyData.remark} onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleCompanyUpdate} disabled={loading}>
                                {loading ? <AiOutlineLoading className={`${Styles.loading_icon}`} /> : 'Update'}
                            </button>
                            <button onClick={() => navigate("/company")}>Cancel</button>
                        </div>
                    </div>
                )}

                {activeTab === 'warehouse' && (
                    <div className={`${Styles.warehouse_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Warehouse details</h4>
                            <hr />
                        </div>

                        {/* Customer ID and Company Name (Read-only) */}
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input}`}>
                                <label>Customer ID (auto-generated)</label>
                                <input type="text" value={companyData.customer_id} disabled />
                            </div>
                            <div className={`${Styles.label_input}`}>
                                <label>Company name</label>
                                <input type="text" value={companyData.company_name} disabled />
                            </div>
                        </div>

                        {/* Warehouse Details Section */}
                        <div className={`${Styles.warehouse_section}`}>
                            <div className={`${Styles.warehouse_header} ${Styles.label_input}`}>
                                <label>Warehouse details</label>
                                <span>Select assign warehouse lot</span>
                            </div>

                            <div className={`${Styles.warehouse_lot_selection}`}>
                                {/* Warehouse Cards */}
                                <div className={`${Styles.warehouse_cards}`}>
                                    {companyData.company_warehouses.length > 0 ? (
                                        companyData.company_warehouses.map((warehouse, index) => {
                                            const warehouseInfo = warehouses.find(w => w.id === warehouse.warehouse_id);
                                            // Check if warehouse has name property (for new warehouses) or get from warehouseInfo
                                            const warehouseName = warehouse.name || (warehouseInfo ? warehouseInfo.name : `Warehouse ${warehouse.warehouse_id}`);

                                            return (
                                                <div
                                                    key={index}
                                                    className={`${Styles.warehouse_card} ${warehouse.is_primary ? Styles.primary : ''}`}
                                                >
                                                    {/* Radio button indicator */}
                                                    <div className={`${Styles.radio_indicator}`}>
                                                        <div className={`${Styles.radio_circle} ${warehouse.is_primary ? Styles.selected : ''}`}>
                                                            {warehouse.is_primary && <div className={`${Styles.radio_dot}`}></div>}
                                                        </div>
                                                    </div>

                                                    {/* Warehouse Info */}
                                                    <div className={`${Styles.warehouse_info}`}>
                                                        <h3>{warehouseName}</h3>
                                                        <p>Lot # {warehouse.min_lot}-{warehouse.max_lot}</p>
                                                        <p>Membership till {new Date(warehouse.membership_till).toLocaleDateString()}</p>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className={`${Styles.warehouse_actions}`}>
                                                        {!warehouse.is_primary && (
                                                            <button
                                                                className={`${Styles.set_primary_btn}`}
                                                                onClick={() => handleSetPrimary(index)}
                                                            >
                                                                Set as primary
                                                            </button>
                                                        )}
                                                        <button
                                                            className={`${Styles.edit_btn}`}
                                                            onClick={() => handleEditWarehouse(index)}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className={`${Styles.no_warehouses}`}>
                                            <p>No warehouses associated with this company</p>
                                        </div>
                                    )}
                                </div>

                                {/* Add New Warehouse Button */}
                                {!isAddingWarehouse && (
                                    <button
                                        className={`${Styles.add_warehouse_btn}`}
                                        onClick={handleAddNewWarehouse}
                                    >
                                        <span>+</span> Add new warehouse lot
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Add New Warehouse Form (Show only when adding) */}
                        {isAddingWarehouse && (
                            <div className={`${Styles.new_warehouse_form}`}>
                                <h3>Add New Warehouse</h3>

                                <div className={`${Styles.form_row}`}>
                                    <div className={`${Styles.label_input}`}>
                                        <label>Membership till<span>*</span></label>
                                        <input
                                            type="date"
                                            name="membership_till"
                                            value={newWarehouseData.membership_till}
                                            onChange={handleNewWarehouseInputChange}
                                        />
                                    </div>

                                    <div className={`${Styles.label_input}`}>
                                        <label>Warehouse location<span>*</span></label>
                                        <select
                                            name="warehouse_id"
                                            value={newWarehouseData.warehouse_id}
                                            onChange={handleNewWarehouseInputChange}
                                        >
                                            <option value="" hidden>Select Warehouse</option>
                                            {warehouses
                                                .filter(w =>
                                                    w.status === 1 &&
                                                    !companyData.company_warehouses.some(cw => cw.warehouse_id === w.id)
                                                )
                                                .map(w => (
                                                    <option key={w.id} value={w.id}>
                                                        {w.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                </div>

                                <div className={`${Styles.form_row}`}>
                                    <div className={`${Styles.label_input}`}>
                                        <label>Lot #<span>*</span></label>
                                        <div className={`${Styles.lot_inputs}`}>
                                            <input
                                                type="text"
                                                placeholder="Minimum"
                                                name="min_lot"
                                                value={newWarehouseData.min_lot}
                                                onChange={handleNewWarehouseInputChange}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Maximum"
                                                name="max_lot"
                                                value={newWarehouseData.max_lot}
                                                onChange={handleNewWarehouseInputChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={`${Styles.form_buttons}`}>
                                    <button
                                        className={`${Styles.save_btn}`}
                                        onClick={handleSaveNewWarehouse}
                                    >
                                        Add Warehouse
                                    </button>
                                    <button
                                        className={`${Styles.cancel_btn}`}
                                        onClick={handleCancelNewWarehouse}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Edit Form (Show only when editing) */}
                        {editingWarehouse !== null && (
                            <div className={`${Styles.edit_form_overlay}`}>
                                <div className={`${Styles.edit_form}`}>
                                    <h3>Edit Warehouse Details</h3>

                                    <div className={`${Styles.form_row}`}>
                                        <div className={`${Styles.label_input}`}>
                                            <label>Membership till<span>*</span></label>
                                            <input
                                                type="date"
                                                name="membership_till"
                                                value={editWarehouseData.membership_till}
                                                onChange={handleEditInputChange}
                                            />
                                        </div>

                                        <div className={`${Styles.label_input}`}>
                                            <label>Warehouse location<span>*</span></label>
                                            <select
                                                name="warehouse_id"
                                                value={editWarehouseData.warehouse_id}
                                                onChange={handleEditInputChange}
                                            >
                                                <option value="" hidden>Select Warehouse</option>
                                                {warehouses.filter(w => w.status === 1).map(w => (
                                                    <option key={w.id} value={w.id}>
                                                        {w.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className={`${Styles.form_row}`}>
                                        <div className={`${Styles.label_input}`}>
                                            <label>Lot #</label>
                                            <div className={`${Styles.lot_inputs}`}>
                                                <input
                                                    type="text"
                                                    placeholder="Minimum"
                                                    name="min_lot"
                                                    value={editWarehouseData.min_lot}
                                                    onChange={handleEditInputChange}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Maximum"
                                                    name="max_lot"
                                                    value={editWarehouseData.max_lot}
                                                    onChange={handleEditInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`${Styles.edit_form_buttons}`}>
                                        <button
                                            className={`${Styles.save_btn}`}
                                            onClick={handleSaveEdit}
                                        >
                                            Save
                                        </button>
                                        <button
                                            className={`${Styles.cancel_btn}`}
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Update Button */}
                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleWarehouseUpdate} disabled={loading}>
                                {loading ? <AiOutlineLoading className={`${Styles.loading_icon}`} /> : 'Update'}
                            </button>
                            <button onClick={() => navigate("/company")}>Cancel</button>
                        </div>
                    </div>
                )}

                {activeTab === 'product' && (
                    <div className={`${Styles.product_details}`}>
                        <h2>Product in stock List</h2>
                        {/* <ProductList /> */}
                        {/* <ProductList companyId={id} filterByCompany={true} /> */}
                        <ProductList companyId={id} companyName={companyData.company_name} filterByCompany={true} />
                    </div>
                )}

                {activeTab === 'report' && (
                    <div className={`${Styles.report_details}`}>
                        <div className={`${Styles.handle_form}`}>
                            <div className={`${Styles.tab}`}>
                                <h4>Company Reports</h4>
                                <hr className={`${Styles.tabBorder}`} />
                            </div>
                        </div>
                        <hr />

                        <div className={`${Styles.report_cards}`}>
                            {/* Sales Analysis Card */}
                            <div className={`${Styles.sales_card}`}>
                                <div className={`${Styles.sales_analysis}`}>
                                    <h1>Sales analysis</h1>
                                    {reportLoading ? (
                                        <p>Loading...</p>
                                    ) : (
                                        <ul>
                                            {salesAnalysisData.map((item, index) => (
                                                <li key={index} style={{ listStyle: 'none' }}>
                                                    <span style={{ color: item.color }}> ● </span>{item.name} {item.value}%
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className={`${Styles.pie_chart}`}>
                                    <ResponsiveContainer width={200} height={200}>
                                        <PieChart>
                                            <Pie data={salesAnalysisData} dataKey="value" cx="50%" cy="50%" outerRadius={40} innerRadius={25} paddingAngle={5}>
                                                {salesAnalysisData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Stock Movement Card */}
                            <div className={`${Styles.stock_card}`}>
                                <div className={`${Styles.content}`}>
                                    <h1>Total stock movement</h1>
                                    {reportLoading ? (
                                        <p>Loading...</p>
                                    ) : (
                                        <>
                                            <h2>{reportData.total_stock_movment?.toLocaleString() || '0'}</h2>
                                            <p><span style={{ color: "green" }}>40%</span> Vs last month</p>
                                        </>
                                    )}
                                </div>
                                <div className={`${Styles.chart}`}>
                                    <ResponsiveContainer width={200} height={100}>
                                        <LineChart data={stockData}>
                                            <XAxis dataKey="name" hide />
                                            <YAxis hide />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="stock" stroke="#3E54AC" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Profit/Loss Graph */}
                        <div className={`${Styles.report_graph}`}>
                            <h2>Company Sales graph</h2>
                            <div className={`${Styles.graph}`}>
                                <div className={`${Styles.graph_content}`}>
                                    <h3>Company Sales graph</h3>
                                    <p>Track your product Sales</p>
                                </div>
                                <div className={`${Styles.bar_chart}`}>
                                    <ResponsiveContainer width="100%" height={450}>
                                        <BarChart data={profitLossData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="month" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="shopify" fill="#A6B1E1" barSize={30} />
                                            <Bar dataKey="lazada" fill="#3E54AC" barSize={30} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CompanyEdit;